/**
 * Colour ramps for the 3D views, and the settings that go with them.
 *
 * Kept in one place because the volume window, the horizontal slice and the
 * vertical section have to agree. A property that reads blue-to-yellow in the
 * map and yellow-to-blue in the section is not two views of one model; it is
 * two pictures that happen to share a title.
 *
 * The clamp matters as much as the ramp. Stretching every view to its own
 * min and max makes two layers look equally variable when one spans 0.02 and
 * the other 0.30 — so the range is editable, and it says whether it is the
 * data's own range or one someone typed in.
 */

export type Stops = [number, number, number][];

/** Named ramps. The first entry of each is the low end. */
export const RAMPS: Record<string, Stops> = {
  // Pale blue upward, never near-black: on white paper a dark low end reads as
  // a hole in the map rather than as a low value.
  porosity: [
    [222, 236, 245], [140, 190, 220], [60, 160, 180], [110, 200, 140], [235, 225, 130],
  ],
  shale: [
    [235, 230, 205], [205, 175, 120], [160, 130, 90], [105, 100, 80], [55, 62, 68],
  ],
  // The blue-green-yellow-red rainbow that permeability and resistivity sheets
  // have used for decades. Not colour-blind friendly, and kept because its
  // readers are trained on it.
  spectrum: [
    [70, 100, 190], [30, 150, 200], [40, 190, 150], [130, 210, 70],
    [235, 220, 60], [240, 150, 45], [220, 60, 50],
  ],
  // Cokelat–jingga–krem yang dipakai tiap penyaji citra lubang bor sejak FMI
  // pertama. Ia ada di sini bukan karena indah melainkan karena pembacanya
  // sudah terlatih membacanya: konduktif gelap, resistif terang, dan mata yang
  // sudah sepuluh tahun membaca rekahan sebagai garis cokelat tua akan
  // kehilangan setengah detik pada tiap garis bila palet ini diganti.
  fmi: [
    [38, 18, 6], [92, 42, 12], [148, 78, 22], [198, 124, 44],
    [226, 168, 84], [242, 208, 148], [252, 240, 214],
  ],
  // Perceptually even, and the safe default when nobody has an opinion.
  viridis: [
    [68, 1, 84], [59, 82, 139], [33, 145, 140], [94, 201, 98], [253, 231, 37],
  ],
  // Diverging. Only meaningful when the midpoint means something — a density
  // either side of 2.4 g/cc, say — so it is offered, not defaulted to.
  redblue: [
    [178, 24, 43], [239, 138, 98], [247, 247, 247], [103, 169, 207], [33, 102, 172],
  ],
  greyscale: [
    [245, 245, 245], [190, 190, 190], [130, 130, 130], [75, 75, 75], [25, 25, 25],
  ],

  // ---------------------------------------------------------------------
  // The OpendTect family.
  //
  // Named and ordered as OpendTect's own colour tables, because people arrive
  // here already knowing what "Seismics" or "Chimney" looks like, and a ramp
  // that carries a familiar name but different colours is worse than one with
  // an unfamiliar name. The stops are read off OpendTect's tables rather than
  // invented; where its tables are smooth gradients, a handful of stops is
  // enough, since everything between them is interpolated anyway.
  //
  // Note the low end of several of these is near-black. That is correct for
  // OpendTect, which is read on a dark background — on the light sheets this
  // application defaults to, a black low end reads as a hole. Both are offered
  // and neither is forced.
  // ---------------------------------------------------------------------

  /** OpendTect "Seismics": the blue-white-red every interpreter reads. */
  ot_seismics: [
    [0, 0, 90], [0, 60, 180], [80, 160, 235], [255, 255, 255],
    [235, 160, 80], [180, 60, 0], [90, 0, 0],
  ],
  /** OpendTect "Rainbow": violet through to red, the full sweep. */
  ot_rainbow: [
    [130, 0, 160], [60, 60, 220], [0, 160, 230], [0, 190, 130],
    [140, 210, 40], [245, 225, 40], [245, 145, 30], [220, 40, 30],
  ],
  /** OpendTect "Grey scales": black at the low end, as it ships. */
  ot_grey: [[0, 0, 0], [64, 64, 64], [128, 128, 128], [192, 192, 192], [255, 255, 255]],
  /** OpendTect "Pastel": muted, for stacking under other layers. */
  ot_pastel: [
    [170, 200, 225], [180, 220, 195], [225, 225, 175], [230, 195, 175], [215, 175, 195],
  ],
  /** OpendTect "Flames": black through red and orange to white. */
  ot_flames: [
    [0, 0, 0], [120, 10, 10], [200, 60, 10], [240, 140, 20],
    [250, 210, 90], [255, 255, 255],
  ],
  /** OpendTect "Cold-Warm": diverging through neutral grey, not white. */
  ot_coldwarm: [
    [0, 120, 190], [110, 175, 215], [200, 200, 200], [230, 150, 90], [190, 60, 20],
  ],
  /** OpendTect "Chimney": dark blue to yellow-green, for likelihood cubes. */
  ot_chimney: [
    [15, 20, 60], [30, 70, 130], [40, 140, 140], [130, 195, 90], [235, 235, 100],
  ],
  /** OpendTect "Faults": white through to near-black. */
  ot_faults: [[255, 255, 255], [190, 190, 200], [120, 125, 140], [60, 65, 80], [15, 15, 25]],
  /** OpendTect "Blue-White-Red", the reverse-polarity twin of Seismics. */
  ot_bluewhitered: [
    [40, 40, 200], [120, 170, 240], [255, 255, 255], [240, 150, 120], [200, 30, 30],
  ],

  // ---------------------------------------------------------------------
  // Keluarga Petrel (SLB).
  //
  // Alasannya sama dengan keluarga OpendTect di atas, dan bukan alasan
  // estetika: orang datang ke sini sudah membawa lembar Petrel di kepalanya.
  // Sebuah penampang yang GR-nya "kuning pasir ke abu serpih" dan sebuah
  // penampang yang GR-nya viridis menceritakan batuan yang sama dengan dua
  // bahasa, dan yang kedua harus diterjemahkan lebih dulu sebelum bisa
  // dibandingkan dengan lembar yang sudah dicetak minggu lalu.
  //
  // Stop-nya dibaca dari tabel warna Petrel — diambil dengan mata dari
  // tabelnya, bukan disalin dari berkasnya — jadi ini kemiripan, bukan
  // salinan: tidak ada satu pun data milik Schlumberger di dalam berkas ini.
  // Yang dijaga adalah hal yang memang dibaca orang tanpa berpikir: urutan
  // huenya, di mana titik tengahnya jatuh, dan ujung mana yang terang.
  // Nama-namanya pun nama Petrel, sebab ramp yang memakai nama yang dikenal
  // tetapi berwarna lain lebih buruk daripada ramp bernama asing.
  // ---------------------------------------------------------------------

  /** Petrel "Seismic default": biru-putih-merah, polaritas yang dibaca semua orang. */
  pt_seismic: [
    [0, 0, 140], [0, 70, 220], [125, 185, 245], [255, 255, 255],
    [245, 175, 120], [215, 55, 35], [130, 0, 0],
  ],
  /** Petrel "Red white blue" — kembaran Seismic default dengan polaritas terbalik. */
  pt_redwhiteblue: [
    [130, 0, 0], [215, 55, 35], [245, 175, 120], [255, 255, 255],
    [125, 185, 245], [0, 70, 220], [0, 0, 140],
  ],
  /** Petrel "Rainbow": ungu ke merah, sapuan penuh yang jadi bawaan atribut. */
  pt_rainbow: [
    [120, 0, 170], [55, 45, 215], [0, 145, 235], [0, 190, 180],
    [45, 200, 75], [190, 220, 40], [250, 215, 45], [245, 140, 35], [220, 35, 35],
  ],
  /** Petrel "Spectrum": rainbow yang ujung merahnya diteruskan sampai putih. */
  pt_spectrum: [
    [40, 20, 120], [30, 90, 200], [0, 165, 200], [55, 185, 120],
    [200, 215, 60], [245, 175, 45], [225, 65, 40], [250, 240, 235],
  ],
  /**
   * Petrel "Gamma ray": kuning pasir di bacaan rendah, abu serpih di tinggi.
   *
   * Ini satu-satunya ramp di sini yang boleh dipakai tanpa legenda, sebab
   * warnanya bukan konvensi melainkan batuannya sendiri — dan itu pula yang
   * membuatnya keliru bila dipakai untuk apa pun selain GR atau Vsh.
   */
  pt_gammaray: [
    [252, 244, 200], [232, 214, 130], [196, 190, 105], [148, 160, 96],
    [104, 116, 88], [66, 70, 66],
  ],
  /** Petrel "Porosity": pucat di ujung rendah, sehingga rapat tidak jadi lubang. */
  pt_porosity: [
    [240, 240, 235], [175, 205, 225], [95, 175, 200], [70, 185, 145],
    [180, 210, 85], [245, 205, 60], [225, 105, 45],
  ],
  /** Petrel "Permeability": dibaca per dekade, jadi tiap dekade dapat huenya sendiri. */
  pt_permeability: [
    [45, 55, 105], [40, 110, 180], [35, 170, 165], [110, 195, 90],
    [235, 215, 60], [240, 140, 45], [205, 45, 40],
  ],
  /**
   * Petrel "Saturation": hangat di Sw rendah, biru di Sw tinggi.
   *
   * Arahnya bukan selera. Air berwarna biru pada tiap lembar yang pernah
   * dicetak, dan ramp yang membalik itu membuat kolom hidrokarbon terbaca
   * sebagai kaki air oleh mata yang belum sempat membaca legendanya.
   */
  pt_saturation: [
    [190, 40, 45], [235, 130, 60], [245, 205, 110], [150, 200, 175],
    [60, 145, 205], [25, 65, 150],
  ],
  /** Petrel "Elevation depth": hijau dataran, cokelat tinggian, putih puncaknya. */
  pt_elevation: [
    [40, 105, 70], [110, 160, 80], [190, 190, 110], [190, 150, 95],
    [150, 110, 85], [190, 180, 175], [250, 250, 250],
  ],
  /** Petrel "Velocity": lambat gelap, cepat terang — lewat biru dan hijau. */
  pt_velocity: [
    [25, 25, 70], [30, 80, 155], [30, 150, 165], [90, 190, 120],
    [215, 210, 90], [250, 245, 225],
  ],
  /** Petrel "Grey scale": hitam di ujung rendah, sebagaimana ia terpasang. */
  pt_grey: [[0, 0, 0], [70, 70, 70], [140, 140, 140], [205, 205, 205], [255, 255, 255]],
};

export const RAMP_LABELS: Record<string, string> = {
  porosity: "Porosity (blue-yellow)",
  shale: "Shale (sand-grey)",
  spectrum: "Spectrum (rainbow)",
  fmi: "FMI (borehole image)",
  viridis: "Viridis",
  redblue: "Red-white-blue",
  greyscale: "Greyscale",
  ot_seismics: "OpendTect · Seismics",
  ot_rainbow: "OpendTect · Rainbow",
  ot_grey: "OpendTect · Grey scales",
  ot_pastel: "OpendTect · Pastel",
  ot_flames: "OpendTect · Flames",
  ot_coldwarm: "OpendTect · Cold-Warm",
  ot_chimney: "OpendTect · Chimney",
  ot_faults: "OpendTect · Faults",
  ot_bluewhitered: "OpendTect · Blue-White-Red",
  pt_seismic: "Petrel · Seismic default",
  pt_redwhiteblue: "Petrel · Red white blue",
  pt_rainbow: "Petrel · Rainbow",
  pt_spectrum: "Petrel · Spectrum",
  pt_gammaray: "Petrel · Gamma ray",
  pt_porosity: "Petrel · Porosity",
  pt_permeability: "Petrel · Permeability",
  pt_saturation: "Petrel · Saturation",
  pt_elevation: "Petrel · Elevation depth",
  pt_velocity: "Petrel · Velocity",
  pt_grey: "Petrel · Grey scale",
};

/* ---------------------------------------------------------------------
 * Palet buatan pengguna, didaftarkan saat aplikasi hidup.
 *
 * `RAMPS` dan `RAMP_LABELS` di atas tetap tabel bawaan yang tidak bisa
 * disentuh: sebuah lembar yang menyebut `spectrum` harus menggambar `spectrum`
 * yang sama di komputer mana pun, dan itu hanya terjamin selama palet rumah
 * tidak bisa menimpanya. Yang ditambahkan di bawah adalah lapisan kedua yang
 * dibaca **sesudah** tabel bawaan.
 *
 * Bentuknya sebuah pendaftaran bermodul dan bukan React context, sebab yang
 * memerlukannya bukan hanya komponen: `LogTracks` melukis pita warnanya di
 * dalam fungsi gambar biasa, jauh dari pohon komponen mana pun, dan sebuah
 * context tidak sampai ke sana tanpa menyeret setiap fungsi gambar ke dalam
 * React. Yang berlangganan diberi tahu lewat `subscribeRamps`, sehingga layar
 * yang sedang terbuka ikut berubah ketika sebuah palet disunting.
 * --------------------------------------------------------------------- */

let userStops: Record<string, Stops> = {};
let userLabels: Record<string, string> = {};
const rampWatchers = new Set<() => void>();
/** Naik setiap kali daftar berubah; dibaca `useSyncExternalStore`. */
let rampVersion = 0;

/** Mengganti seluruh daftar palet pengguna dan memberi tahu yang berlangganan. */
export function registerUserRamps(ramps: { key: string; label: string; stops: Stops }[]): void {
  userStops = Object.fromEntries(ramps.map((r) => [r.key, r.stops]));
  userLabels = Object.fromEntries(ramps.map((r) => [r.key, r.label]));
  rampVersion += 1;
  rampWatchers.forEach((fn) => fn());
}

export function subscribeRamps(fn: () => void): () => void {
  rampWatchers.add(fn);
  return () => rampWatchers.delete(fn);
}

/** Cap versi daftar palet. Berubah berarti ada yang ditambah, disunting atau dibuang. */
export function rampsVersion(): number {
  return rampVersion;
}

/** Semua kunci palet: bawaan lebih dulu, lalu buatan pengguna. */
export function rampKeys(): string[] {
  return [...Object.keys(RAMPS), ...Object.keys(userStops)];
}

/**
 * Stop sebuah palet menurut kuncinya.
 *
 * Kunci yang tidak dikenal memulangkan palet bawaan pertama alih-alih melempar
 * atau memulangkan kosong. Kunci semacam itu memang terjadi: sebuah lembar yang
 * menyebut palet rumah lalu dibuka di komputer yang belum punya palet itu.
 * Yang benar di sana adalah menggambar sesuatu yang terbaca, bukan track kosong
 * tanpa sebab yang kelihatan.
 */
export function rampStops(key: string): Stops {
  // Bawaan lebih dulu, dan itulah yang membuat janji di kepala bagian ini
  // berlaku: sebuah palet rumah tidak bisa menimpa `spectrum` sekalipun
  // kuncinya berhasil menyerupainya. Awalan `user_` sudah menutup jalan itu di
  // `normalizeUserRamps`; urutan di sini menutupnya untuk kedua kalinya, sebab
  // yang menjaga sebuah janji sebaiknya bukan hanya satu penjaga.
  return RAMPS[key] ?? userStops[key] ?? RAMPS[Object.keys(RAMPS)[0]];
}

/** Nama sebuah palet; kunci tak dikenal memulangkan kuncinya sendiri. */
export function rampLabel(key: string): string {
  return RAMP_LABELS[key] ?? userLabels[key] ?? key;
}

/**
 * Daftar palet pengguna sebagaimana terdaftar saat ini.
 *
 * Inilah yang membuat pendaftaran ini menjadi satu-satunya sumber kebenaran
 * alih-alih sekadar cermin. `useStored` menyimpan satu state per pemanggil dan
 * tidak menyelaraskan keduanya, jadi dua layar yang sama-sama membaca kunci
 * penyimpanan yang sama akan berselisih begitu salah satunya menulis — dan
 * yang kalah menuliskan daftarnya yang basi kembali ke peramban. Dengan
 * membaca dari sini, setiap pemanggil melihat daftar yang sama persis.
 */
export function userRampList(): { key: string; label: string; stops: Stops }[] {
  return Object.keys(userStops).map((key) => ({
    key,
    label: userLabels[key] ?? key,
    stops: userStops[key],
  }));
}

/**
 * Gradasi CSS satu palet, sebagaimana setiap pemilih menggambarnya.
 *
 * Ungkapan ini sebelumnya disalin di empat tempat — dua di `LogQuickEdit`, satu
 * di `TrackEditor`, satu di `ColorBar` — dan keempatnya membaca `RAMPS`
 * langsung. Salinan itulah yang membuat palet buatan pengguna harus ditambahkan
 * empat kali untuk muncul di semua tempat, dan yang terlewat satu akan menjadi
 * pemilih yang diam-diam kehilangan separuh pilihannya.
 */
export function rampGradient(key: string, reverse = false): string {
  const stops = rampStops(key);
  const akhir = Math.max(1, stops.length - 1);
  const warna = stops.map((_, i) => rampColor(stops, i / akhir));
  return `linear-gradient(to ${reverse ? "left" : "right"}, ${warna.join(", ")})`;
}

/** The ramp a curve gets before anyone changes it. */
export function defaultRamp(property: string): string {
  if (property === "VSH") return "shale";
  if (property === "PERM" || property === "RT") return "spectrum";
  if (property === "RHOB") return "redblue";
  return "porosity";
}

export interface ColorScale {
  ramp: string;
  reverse: boolean;
  /** null means "follow the data", which is also what the label then says. */
  min: number | null;
  max: number | null;
}

export function defaultScale(property: string): ColorScale {
  return { ramp: defaultRamp(property), reverse: false, min: null, max: null };
}

export function rampColor(stops: Stops, t: number): string {
  if (!Number.isFinite(t)) return "transparent";
  const x = Math.min(0.999, Math.max(0, t)) * (stops.length - 1);
  const i = Math.floor(x);
  const f = x - i;
  const a = stops[i];
  const b = stops[Math.min(stops.length - 1, i + 1)];
  const c = a.map((v, k) => Math.round(v + (b[k] - v) * f));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

/**
 * Turn a scale plus the data's own range into one function every view calls.
 *
 * Returning a closure rather than passing the settings around means the volume
 * window, the map and the section cannot drift apart: there is only one place
 * that decides what a value looks like.
 */
export function makePainter(scale: ColorScale, dataMin: number, dataMax: number) {
  // Lewat `rampStops`, bukan `RAMPS` langsung: inilah fungsi yang mewarnai
  // volume, peta horizon, penampang dan pita warna lembar log, jadi palet
  // buatan pengguna yang tidak sampai ke sini adalah palet yang bisa dipilih
  // di mana-mana tetapi tidak menggambar apa pun.
  const stops = rampStops(scale.ramp);
  const lo = scale.min ?? dataMin;
  const hi = scale.max ?? dataMax;
  const span = hi - lo || 1;
  return {
    lo,
    hi,
    /** Colour for a value in the property's own units. */
    at(value: number): string {
      const t = (value - lo) / span;
      return rampColor(stops, scale.reverse ? 1 - t : t);
    },
    /** Colour for an already-normalised position, for drawing the bar itself. */
    atFraction(t: number): string {
      return rampColor(stops, scale.reverse ? 1 - t : t);
    },
  };
}

/**
 * A round number of metres for a scale bar, and how wide it is on screen.
 *
 * `auto` picks the 1-2-5 step nearest a quarter of the view, which is the
 * length that reads without crowding. A fixed choice is honoured as given —
 * someone comparing two sheets wants the same bar on both, not whatever each
 * one thought looked best.
 */
export function scaleBar(metresAcross: number, pixelsAcross: number, fixed?: number | null) {
  const target = fixed ?? metresAcross / 4;
  const steps = [1, 2, 5];
  let best = 1;
  for (let exp = 0; exp <= 7; exp++) {
    for (const step of steps) {
      const candidate = step * 10 ** exp;
      if (candidate <= target * 1.35) best = candidate;
    }
  }
  const metres = fixed ?? best;
  return {
    metres,
    pixels: (metres / metresAcross) * pixelsAcross,
    label: metres >= 1000 ? `${(metres / 1000).toFixed(metres % 1000 ? 1 : 0)} km` : `${metres} m`,
  };
}

/**
 * Angka pada bar warna, dalam satu bentuk di mana pun barnya digambar.
 *
 * Porositas 0,182 dan permeabilitas 1240 mD tidak bisa memakai jumlah desimal
 * yang sama, jadi yang dipakai sebanyak yang masih berarti pada besarannya.
 * Satu tempat saja, sebab bar di rel dan bar di atas kanvas harus membaca
 * ujung yang sama dengan cara yang sama.
 */
export function formatValue(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toFixed(0);
  if (abs >= 10) return value.toFixed(1);
  if (abs >= 1) return value.toFixed(2);
  return value.toFixed(3);
}
